// Friends in a shared world: smoothed avatars, gestures and speech bubbles. Networking and audio live elsewhere.
import * as THREE from 'three';
import { Character } from './character.js';
import { paintSkin, SKIN_TONES, HAIR_COLORS } from './skins.js';
import { catalogItem } from './progression.js';
import { REGION, regionKey } from './discovery-terrain.js';
import { clamp, dampAngle } from './util.js';
import { EMOTES, EMOTE_ICONS } from './friends-ui.js';

const SNAP = 6, VIEW = 70, LAMBDA = 12, GESTURE = 1.6, BUBBLE = 2.5, HEAD = 2.55, SWING = 0.36;
const STALE = 0.6; // seconds without a position update before a peer settles into idle
const STYLES = ['short', 'spiky', 'long'];
const _v = new THREE.Vector3();
const fin = (v) => typeof v === 'number' && Number.isFinite(v);
const index = (v, n, d) => (fin(v) ? clamp(Math.round(v), 0, n - 1) : d);

function lookOf(peer) {
  const a = peer.avatar || {}, e = peer.equip || {};
  return {
    skin: catalogItem('skin', e.skin) ? e.skin : 'classic',
    sword: catalogItem('sword', e.sword) ? e.sword : 'wood',
    tone: index(a.tone, SKIN_TONES.length, 3),
    hair: index(a.hair, HAIR_COLORS.length, 0),
    style: STYLES.includes(a.style) ? a.style : 'short',
  };
}

// Swords and accessories own their geometry; Character.dispose() frees materials but not these.
function dropCharacter(ch) {
  ch.root.removeFromParent();
  for (const o of [ch.item, ch.accessory]) o?.traverse((c) => c.geometry?.dispose());
  ch.dispose();
}

export class PeerManager {
  constructor(game, explore) {
    this.game = game;
    this.explore = explore;
    this.skins = game?.skinCache || new Map();
    this.peers = new Map();
    this.seq = 0;
  }

  get count() {
    return this.peers.size;
  }
  get worldUI() {
    return this.explore?.fx?.worldUI;
  }
  has(id) {
    return this.peers.has(String(id));
  }

  setAll(peers = []) {
    const keep = new Set();
    for (const p of peers || []) if (p?.id != null && this.add(p) !== null) keep.add(String(p.id));
    for (const id of [...this.peers.keys()]) if (!keep.has(id)) this.remove(id);
  }

  // Returns true for a new peer, false for an update, null for bad input.
  add(peer) {
    if (peer?.id == null) return null;
    const id = String(peer.id), look = lookOf(peer);
    const name = String(peer.name ?? '').trim().slice(0, 16) || 'Friend';
    const sig = [name, look.skin, look.tone, look.hair, look.style, look.sword].join('|');
    let rec = this.peers.get(id);
    const isNew = !rec;
    if (isNew) {
      rec = { id, name, sig: '', ch: null, has: false, at: new THREE.Vector3(), face: 0, target: new THREE.Vector3(), yaw: 0,
        st: { vy: 0, g: 1, sp: 0, r: 0 }, swing: -1, lastSw: -1, age: 0, waveT: 0, partyT: 0, bubble: '', bubbleT: 0, shown: false, visible: false };
      this.peers.set(id, rec);
    }
    rec.name = name;
    if (rec.sig !== sig) {
      this.build(rec, look);
      rec.sig = sig;
    }
    const p = peer.pos;
    if (Array.isArray(p) && p.length >= 3) this.pos(id, { x: p[0], y: p[1], z: p[2], yw: p[3] });
    return isNew;
  }

  build(rec, look) {
    if (rec.ch) dropCharacter(rec.ch);
    const key = `${look.skin}|${look.tone}|${look.hair}|${look.style}`;
    if (!this.skins.has(key)) this.skins.set(key, paintSkin(look.skin, look.tone, look.hair, look.style));
    const ch = new Character(this.skins.get(key), { name: rec.name.toUpperCase(), crown: false, tagScale: 0.85, tagColor: '#9fd8ff' });
    ch.setItem(look.sword);
    const acc = catalogItem('skin', look.skin)?.acc;
    if (acc) ch.setAccessory(acc);
    ch.root.position.copy(rec.at);
    ch.root.rotation.y = rec.face;
    ch.root.visible = false;
    this.explore.group.add(ch.root);
    rec.ch = ch;
  }

  remove(id) {
    id = String(id);
    const rec = this.peers.get(id);
    if (!rec) return false;
    this.worldUI?.hide('peer:' + id);
    if (rec.ch) dropCharacter(rec.ch);
    this.peers.delete(id);
    return true;
  }

  // p: {x,y,z,yw,vy,g,sw,sp,r}. Snaps on the first position or a jump of more than 6 blocks.
  pos(id, p) {
    const rec = this.peers.get(String(id));
    if (!rec || !p || !fin(p.x) || !fin(p.y) || !fin(p.z)) return false;
    rec.target.set(p.x, p.y, p.z);
    if (fin(p.yw)) rec.yaw = p.yw;
    if (!rec.has || rec.at.distanceTo(rec.target) > SNAP) {
      rec.at.copy(rec.target);
      rec.face = rec.yaw;
      rec.has = true;
    }
    const s = rec.st;
    s.vy = fin(p.vy) ? p.vy : 0;
    s.g = p.g === undefined || p.g ? 1 : 0;
    s.sp = fin(p.sp) ? Math.max(0, p.sp) : 0;
    s.r = p.r ? 1 : 0;
    // Swings are sampled at 8 Hz, so play each new one locally at full frame rate.
    const sw = fin(p.sw) ? p.sw : -1;
    if (sw >= 0 && (rec.swing < 0 || sw < rec.lastSw)) rec.swing = Math.min(sw, 0.99);
    rec.lastSw = sw;
    rec.age = 0;
    return true;
  }

  emote(id, k) {
    const rec = this.peers.get(String(id));
    if (!rec || !EMOTES.includes(k)) return false;
    if (k === 'wave') rec.waveT = GESTURE;
    if (k === 'dance' || k === 'cheer') rec.partyT = GESTURE;
    // A fresh counter makes repeated emotes replay their pop animation.
    this.bubbleOn(rec, `<span class="peer-emote" data-n="${++this.seq}">${EMOTE_ICONS[k]}</span>`, BUBBLE);
    return true;
  }

  // html is trusted markup: escape any names before calling.
  say(id, html, seconds = 3) {
    const rec = this.peers.get(String(id));
    if (!rec) return false;
    this.bubbleOn(rec, String(html ?? ''), fin(seconds) && seconds > 0 ? seconds : 3);
    return true;
  }

  bubbleOn(rec, html, seconds) {
    rec.bubble = html;
    rec.bubbleT = seconds;
  }

  update(dt) {
    const ex = this.explore, me = ex?.player?.pos, loaded = ex?.stream?.loaded, ui = this.worldUI;
    const k = 1 - Math.exp(-LAMBDA * dt);
    for (const rec of this.peers.values()) {
      rec.age += dt;
      rec.waveT -= dt;
      rec.partyT -= dt;
      if (rec.bubbleT > 0) rec.bubbleT -= dt;
      if (rec.swing >= 0 && (rec.swing += dt / SWING) >= 1) rec.swing = -1;
      if (rec.has) {
        rec.at.lerp(rec.target, k);
        rec.face = dampAngle(rec.face, rec.yaw, LAMBDA, dt);
      }
      const at = rec.at;
      rec.visible = rec.has && !!me && !!loaded?.has(regionKey(Math.floor(at.x / REGION), Math.floor(at.z / REGION))) && at.distanceTo(me) <= VIEW;
      const ch = rec.ch;
      ch.root.visible = rec.visible;
      if (rec.visible) {
        ch.root.position.copy(at);
        ch.root.rotation.y = rec.face;
        const s = rec.st, fresh = rec.age < STALE;
        ch.update(dt, { speed: fresh ? s.sp : 0, grounded: fresh ? !!s.g : true, vy: s.vy, swing: rec.swing, riding: !!s.r, wave: rec.waveT > 0, celebrate: rec.partyT > 0 });
      }
      if (rec.visible && rec.bubbleT > 0) {
        ui?.anchor('peer:' + rec.id, rec.bubble, _v.set(at.x, at.y + HEAD, at.z), 'bubble');
        rec.shown = true;
      } else if (rec.shown) {
        ui?.hide('peer:' + rec.id);
        rec.shown = false;
      }
    }
  }

  // Map-friendly positions; x and z are null until a peer's first position arrives.
  list() {
    return [...this.peers.values()].map((r) => ({ id: r.id, name: r.name, x: r.has ? r.at.x : null, z: r.has ? r.at.z : null, visible: r.visible }));
  }

  dispose() {
    for (const id of [...this.peers.keys()]) this.remove(id);
  }
}
